import styled from "@emotion/styled/macro";
import { useState } from "react";
import {
  Button,
  Select,
  tokens,
  LoadingIndicator,
} from "@sunrun/experience-ui-components";
import { handleOktaAuthCallback, setJwtCache } from "services/jwtCache";
import {
  generatePkceCodes,
  storePkceVerifier,
  getOauthAuthorizeUrl,
} from "services/splatAuthService";
import { postMessage } from "messages";
import { Mixpanel } from "providers/mixpanel";
import { AUTH__SUNRUN_LOGIN_INITIATED } from "constants/mixpanelEvents";

const INSIDE = "inside";
const STREET = "street";
const FUSION = "fusion";
const CEA = "cea";
const CXPRO = "cx-pro";

type LoginTypes =
  | typeof INSIDE
  | typeof STREET
  | typeof FUSION
  | typeof CEA
  | typeof CXPRO;

const OktaForm = () => {
  const [isPending, setIsPending] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const login = async () => {
    setIsPending(true);
    try {
      const authCallback = handleOktaAuthCallback();
      window.open("/okta");
      const tokenState = await authCallback;
      setJwtCache(tokenState);
      postMessage({
        type: "EVENT:AUTH_TOKEN",
        source: "Auth",
        payload: {
          tokenType: "okta",
          token: tokenState.token,
        },
      });
      postMessage({
        type: "EVENT:LOGIN_SUCCESS",
        source: "Auth",
      });
    } finally {
      setIsPending(false);
    }
  };
  let defaultLogin = STREET;
  const storedLogin = localStorage.getItem("lastSplatLogin");
  if (storedLogin) {
    defaultLogin = storedLogin;
  }

  const getLabelForValue = (value: LoginTypes) => {
    return options.find((option) => option.value === value)?.label;
  };

  const [loginType, setLoginType] = useState(defaultLogin);
  const doSplatLogin = async () => {
    Mixpanel.track(AUTH__SUNRUN_LOGIN_INITIATED, {
      loginType,
      loginLabel: getLabelForValue(loginType as LoginTypes),
    });
    localStorage.setItem("lastSplatLogin", loginType);
    const doPkceLogin = async (isD2H: boolean) => {
      try {
        setIsPending(true);
        const { codeVerifier, codeChallenge } = await generatePkceCodes();
        storePkceVerifier(codeVerifier);
        const { authorizeUrl } = await getOauthAuthorizeUrl(
          codeChallenge,
          isD2H,
          false
        );
        postMessage({
          type: "COMMAND:LOGIN_REDIRECT",
          source: "Auth",
          payload: { url: authorizeUrl },
        });
      } catch {
        setIsPending(false);
      }
    };

    switch (loginType) {
      case INSIDE:
        await doPkceLogin(false);
        break;
      case STREET:
        await doPkceLogin(true);
        break;
      case FUSION:
        await doPkceLogin(true);
        break;
      case CEA:
      case CXPRO:
        login();
        break;
    }
  };

  const options = [
    { value: STREET, label: "Street" },
    { value: INSIDE, label: "Chain & AIR" },
    { value: FUSION, label: "The Block" },
    { value: CEA, label: "Retail" },
    { value: CXPRO, label: "CX Pro" },
  ];

  const initialOption =
    options.find((option) => option.value === loginType) || options[0];

  return (
    <SignOn>
      <Select
        value={initialOption.value}
        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
          setLoginType(event.target.value as LoginTypes);
        }}
        onBlur={() => {}}
        options={options}
        disabled={isPending}
      />

      <LoginButtonContainer>
        <Button size="md" onClick={doSplatLogin}>
          {isPending ? (
            <LoadingIndicator showLoadingMessage={false} color="white" />
          ) : (
            "Log In"
          )}
        </Button>
      </LoginButtonContainer>
    </SignOn>
  );
};

const SignOn = styled.div`
  margin-top: 38px;
  margin-right: 4px;
  > .sr-select-container {
    margin-bottom: 16px;
    button {
      color: ${tokens.TINTS_OFF_WHITE_20};
    }
  }
  > button {
    background: #181e3a;
    border-radius: 800px;
    border: none;
    cursor: pointer;
    padding: 18px;
    position: relative;
    width: 100%;
    &:disabled {
      cursor: default;
      background: #181e3a50;
    }
  }
`;

const LoginButtonContainer = styled.div`
  margin-top: 24px;
  & > button {
    width: 100%;
  }
`;

export { OktaForm };
